import type { ReactNode, FunctionComponent } from 'react';
import { twMerge } from 'src/utils/twMerge';
import { notificationBadgeStyle } from 'src/designSystem/utils/notificationBadgeStyle';

export type NotificationBadgeTone = 'alert' | 'success' | 'reward';

type Props = {
  count?: number;
  children?: ReactNode;
  tone?: NotificationBadgeTone;
  size?: number;
  maxCount?: number | null;
  anchorRadius?: number;
  className?: string;
  'data-testid'?: string;
};
export type { Props as NotificationBadgeProps };

const TONE_CLASSES: Record<NotificationBadgeTone, string> = {
  alert: 'bg-red-darkest text-white',
  success: 'bg-green-dark text-structural-lightest',
  reward: 'bg-warning-neutral text-structural-darkest',
};

const NotificationBadge: FunctionComponent<Props> = ({
  count,
  children,
  tone = 'alert',
  size,
  maxCount = 9,
  anchorRadius,
  className,
  'data-testid': dataTestId,
}) => {
  const hasCount = count !== undefined;
  if (hasCount && count <= 0) return null;

  const canGrow = maxCount === null;
  const isDot = !hasCount && children === undefined;

  return (
    <span
      aria-hidden={isDot || undefined}
      data-testid={dataTestId}
      className={twMerge(
        'pointer-events-none flex items-center justify-center rounded-full',
        !isDot && 'text-xs font-bold leading-none',
        canGrow && 'px-1.5',
        anchorRadius !== undefined && 'absolute',
        TONE_CLASSES[tone],
        className,
      )}
      style={notificationBadgeStyle(size, canGrow, anchorRadius)}
    >
      {hasCount && (!canGrow && count > maxCount ? `${maxCount}+` : count)}
      {children}
    </span>
  );
};

export default NotificationBadge;
